import React, { useCallback, useMemo, useState } from 'react';

import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridColumnGroupingModel,
  GridOverlay,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
} from '@mui/x-data-grid-pro';

import GridPagination from '~/components/Common/Elements/GridPagination';
import EmailFormModal from '~/components/Emails/EmailFormModal';

import { EmailDeliveryFailureRecord } from '~/services/SailthruService';
import { formatDateWithClock } from '~/services/TimeService';

import EmailDeliveryFailureResolutionModal, {
  EmailDeliveryFailureResolutionTarget,
} from './EmailDeliveryFailureResolutionModal';
import { EMAIL_DELIVERY_FAILURES_PAGE_SIZE } from './constants';

interface Props {
  autoHeight?: boolean;
  rows: Array<EmailDeliveryFailureRecord>;
  total: number;
  isLoading: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onResend: (row: EmailDeliveryFailureRecord) => void;
  onResendSelected: () => void;
  resendingNotificationId?: string;
  currentUserEmail?: string;
  selectedNotificationIds: GridRowSelectionModel;
  onSelectionChange: (model: GridRowSelectionModel) => void;
  isResending: boolean;
  lookbackDays: number;
}

const NON_FILTERABLE_COLUMN_PROPS = {
  disableColumnMenu: true,
  filterable: false,
};

const columnGroupingModel: GridColumnGroupingModel = [
  {
    groupId: 'deliverability',
    headerName: 'Deliverability',
    children: [{ field: 'deliveredAt' }, { field: 'droppedAt' }, { field: 'bouncedAt' }, { field: 'failedReason' }],
  },
  {
    groupId: 'engagementEvent',
    headerName: 'Engagement',
    children: [{ field: 'openedAt' }, { field: 'clickedAt' }],
  },
];

function formatDate(value: string | null) {
  return value ? formatDateWithClock(value) : '-';
}

export default function EmailDeliveryFailuresTable({
  autoHeight = false,
  rows,
  total,
  isLoading,
  paginationModel,
  onPaginationModelChange,
  onResend,
  onResendSelected,
  resendingNotificationId,
  currentUserEmail,
  selectedNotificationIds,
  onSelectionChange,
  isResending,
  lookbackDays,
}: Props) {
  const [forwardEmailRecord, setForwardEmailRecord] = useState<EmailDeliveryFailureRecord | null>(null);
  const [forwardNotificationIds, setForwardNotificationIds] = useState<Array<string>>([]);
  const [resolveTargets, setResolveTargets] = useState<Array<EmailDeliveryFailureResolutionTarget>>([]);

  const handleCloseForwardModal = useCallback(() => {
    setForwardEmailRecord(null);
    setForwardNotificationIds([]);
  }, []);

  const handleCloseResolveModal = useCallback(() => {
    setResolveTargets([]);
  }, []);

  const handleResolveSuccess = useCallback(() => {
    onSelectionChange([]);
    setResolveTargets([]);
  }, [onSelectionChange]);

  const handleMarkSelectedResolved = useCallback(() => {
    setResolveTargets(
      selectedNotificationIds.map((notificationId) => ({
        notificationId: String(notificationId),
      })),
    );
  }, [selectedNotificationIds]);

  const handleMarkRowResolved = useCallback((row: EmailDeliveryFailureRecord) => {
    setResolveTargets([
      {
        notificationId: row.notificationId,
        email: row.email,
      },
    ]);
  }, []);

  const renderToolbar = useCallback(
    () => (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarExport />
        <Button
          disabled={selectedNotificationIds.length === 0 || isResending}
          onClick={onResendSelected}
          size="small"
          startIcon={<ReplayOutlinedIcon />}
        >
          Resend selected ({selectedNotificationIds.length})
        </Button>
        <Button
          disabled={selectedNotificationIds.length === 0}
          onClick={() => setForwardNotificationIds(selectedNotificationIds.map(String))}
          size="small"
          startIcon={<ForwardToInboxOutlinedIcon />}
        >
          Forward selected ({selectedNotificationIds.length})
        </Button>
        <Button
          disabled={selectedNotificationIds.length === 0}
          onClick={handleMarkSelectedResolved}
          size="small"
          startIcon={<MarkEmailReadOutlinedIcon />}
        >
          Mark as resolved ({selectedNotificationIds.length})
        </Button>
      </GridToolbarContainer>
    ),
    [handleMarkSelectedResolved, isResending, onResendSelected, selectedNotificationIds],
  );

  const renderNoRowsOverlay = useCallback(
    () => (
      <GridOverlay>
        <Stack alignItems="center" spacing={1.5} sx={{ maxWidth: 420, px: 3, textAlign: 'center' }}>
          <MarkEmailReadOutlinedIcon color="success" sx={{ fontSize: 48 }} />
          <Typography variant="subtitle1">No email delivery failures found</Typography>
          <Typography color="text.secondary" variant="body2">
            All emails were delivered in the last {lookbackDays} days, or all delivery failures have already been
            handled.
          </Typography>
        </Stack>
      </GridOverlay>
    ),
    [lookbackDays],
  );

  const columns: Array<GridColDef<EmailDeliveryFailureRecord>> = useMemo(
    () => [
      {
        ...NON_FILTERABLE_COLUMN_PROPS,
        field: 'notificationId',
        headerName: 'Notification ID',
        width: 300,
      },
      {
        ...NON_FILTERABLE_COLUMN_PROPS,
        field: 'notificationType',
        headerName: 'Notification Type',
        width: 300,
      },
      {
        ...NON_FILTERABLE_COLUMN_PROPS,
        field: 'notificationExtKey',
        headerName: 'External Key',
        width: 260,
      },
      {
        ...NON_FILTERABLE_COLUMN_PROPS,
        field: 'email',
        headerName: 'Email',
        width: 260,
      },
      {
        ...NON_FILTERABLE_COLUMN_PROPS,
        field: 'deliveredAt',
        headerName: 'Delivered',
        width: 160,
        renderCell: ({ value }: GridRenderCellParams<EmailDeliveryFailureRecord, string | null>) => formatDate(value),
      },
      {
        ...NON_FILTERABLE_COLUMN_PROPS,
        field: 'droppedAt',
        headerName: 'Dropped',
        width: 160,
        renderCell: ({ value }: GridRenderCellParams<EmailDeliveryFailureRecord, string | null>) => formatDate(value),
      },
      {
        ...NON_FILTERABLE_COLUMN_PROPS,
        field: 'bouncedAt',
        headerName: 'Bounced',
        width: 160,
        renderCell: ({ value }: GridRenderCellParams<EmailDeliveryFailureRecord, string | null>) => formatDate(value),
      },
      {
        ...NON_FILTERABLE_COLUMN_PROPS,
        field: 'failedReason',
        headerName: 'Reason for Failure',
        width: 240,
        renderCell: ({ value }: GridRenderCellParams<EmailDeliveryFailureRecord, string | null>) => value || '-',
      },
      {
        ...NON_FILTERABLE_COLUMN_PROPS,
        field: 'openedAt',
        headerName: 'Opened',
        width: 160,
        renderCell: ({ value }: GridRenderCellParams<EmailDeliveryFailureRecord, string | null>) => formatDate(value),
      },
      {
        ...NON_FILTERABLE_COLUMN_PROPS,
        field: 'clickedAt',
        headerName: 'Clicked',
        width: 160,
        renderCell: ({ value }: GridRenderCellParams<EmailDeliveryFailureRecord, string | null>) => formatDate(value),
      },
      {
        ...NON_FILTERABLE_COLUMN_PROPS,
        field: 'actions',
        headerName: 'Actions',
        width: 180,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<EmailDeliveryFailureRecord>) => (
          <Stack direction="row" spacing={1}>
            <Tooltip title="View email">
              <IconButton
                aria-label="View email"
                color="primary"
                component="a"
                href={`/email/render?id=${row.notificationId}`}
                rel="noreferrer"
                size="small"
                target="_blank"
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Resend email">
              <span>
                <IconButton
                  aria-label="Resend email"
                  color="primary"
                  disabled={resendingNotificationId === row.notificationId}
                  onClick={() => onResend(row)}
                  size="small"
                >
                  <ReplayOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Forward email">
              <IconButton
                aria-label="Forward email"
                color="primary"
                onClick={() => setForwardEmailRecord(row)}
                size="small"
              >
                <ForwardToInboxOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Mark as resolved">
              <IconButton
                aria-label="Mark as resolved"
                color="primary"
                onClick={() => handleMarkRowResolved(row)}
                size="small"
              >
                <MarkEmailReadOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [handleMarkRowResolved, onResend, resendingNotificationId],
  );

  return (
    <Box
      sx={{ flex: autoHeight ? undefined : 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <DataGridPro
        autoHeight={autoHeight}
        rows={rows}
        columns={columns}
        columnGroupingModel={columnGroupingModel}
        density="compact"
        getRowId={(row) => row.notificationId}
        loading={isLoading}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[EMAIL_DELIVERY_FAILURES_PAGE_SIZE]}
        rowCount={total}
        slots={{ noRowsOverlay: renderNoRowsOverlay, pagination: GridPagination, toolbar: renderToolbar }}
        checkboxSelection
        rowSelectionModel={selectedNotificationIds}
        onRowSelectionModelChange={onSelectionChange}
        disableRowSelectionOnClick
        initialState={{
          columns: {
            columnVisibilityModel: {
              notificationId: false,
              notificationExtKey: false,
            },
          },
        }}
        sx={{
          flex: autoHeight ? undefined : 1,
          height: autoHeight ? 'auto' : '100%',
          '--DataGrid-overlayHeight': autoHeight ? '240px' : undefined,
          minHeight: 0,
          width: '100%',
          border: 0,
          borderRadius: 1,
          '& .MuiDataGrid-overlayWrapper': {
            minHeight: 240,
          },
        }}
      />

      {forwardEmailRecord && (
        <EmailFormModal
          open
          handleClose={handleCloseForwardModal}
          notificationId={forwardEmailRecord.notificationId}
          notificationType={forwardEmailRecord.notificationType}
          initialEmail={currentUserEmail}
          restrictToLuxuryEscapesDomain={false}
        />
      )}

      {forwardNotificationIds.length > 0 && (
        <EmailFormModal
          open
          handleClose={handleCloseForwardModal}
          notificationIds={forwardNotificationIds}
          notificationType="Selected email delivery failures"
          initialEmail={currentUserEmail}
          restrictToLuxuryEscapesDomain={false}
        />
      )}

      {resolveTargets.length > 0 && (
        <EmailDeliveryFailureResolutionModal
          open
          onClose={handleCloseResolveModal}
          targets={resolveTargets}
          onResolved={handleResolveSuccess}
        />
      )}
    </Box>
  );
}
